import React, { useState, useEffect } from 'react';
import { getAllUsersAPI, toggleUserStatusAPI, deleteUserAPI } from '../services/serverService';
import EditUserModal from '../components/EditUserModal';
import AddGlobalAdminModal from '../components/AddGlobalAdminModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

const GlobalUsersPage = () => {
    const { userRole, currentUser, dynamicServerId: myServerId } = useAuth();
    
    const canViewAll = hasPermission(userRole, 'CAN_VIEW_GLOBAL_DASHBOARD');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');
    const canManageAdmins = hasPermission(userRole, 'CAN_MANAGE_GLOBAL_ADMINS');
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [showAddAdminModal, setShowAddAdminModal] = useState(false);

    const fetchAllUsers = async () => {
        setIsLoading(true);
        try {
            const response = await getAllUsersAPI();
            const usersData = response.data.data;
            setAllUsers(usersData);
        } catch (error) {
            console.error("Failed to load data", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllUsers();
    }, []);

    // --- UPDATED ACTIONS WITH REASON CAPTURE ---

    const handleToggleStatus = async (user) => {
        const isUserActive = user.active !== undefined ? user.active === true : user.status === true;
        const actionText = isUserActive ? "disable" : "enable";
        
        const reason = window.prompt(`Are you sure you want to ${actionText} ${user.fullName} globally?\n\nPlease enter a reason (Required):`);
        
        if (reason === null) return; 
        if (reason.trim() === "") return alert("Action cancelled: A reason is required.");

        try { 
            const targetId = user.username || user.id || user.uid || user.email;
            await toggleUserStatusAPI({ 
                userId: targetId,
                uid: targetId, 
                serverId: user.serverId || null, 
                role: user.role || null,
                reason: reason.trim() 
            }); 
            fetchAllUsers(); 
        } 
        catch (error) { alert("Failed to toggle status."); }
    };
    
    const handleDeleteUser = async (user) => {
        if (!canDelete) return alert("Permission Denied");
        
        const confirm1 = window.confirm(`WARNING: PERMANENTLY DELETE ${user.fullName}? This cannot be undone.`);
        if (!confirm1) return;

        const reason = window.prompt("Please enter a reason for global deletion (Required):");
        
        if (reason === null) return;
        if (reason.trim() === "") return alert("Action cancelled: A reason is required.");

        try { 
            const targetId = user.username || user.id || user.uid || user.email;
            await deleteUserAPI({ 
                userId: targetId,
                uid: targetId, 
                serverId: user.serverId || null, 
                role: user.role || null,
                reason: reason.trim() 
            }); 
            fetchAllUsers(); 
        } 
        catch (error) { alert("Error: " + error.message); }
    };

    const filteredUsers = allUsers.filter(user => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = (
            (user.fullName || "").toLowerCase().includes(term) ||
            (user.email || "").toLowerCase().includes(term) ||
            (user.username || "").toLowerCase().includes(term) ||
            (user.role || "").toLowerCase().includes(term)
        );

        if (canViewAll) {
            return matchesSearch; 
        } else {
            if (myServerId && user.serverId) {
                return matchesSearch && (user.serverId === myServerId);
            }
            const myDomain = currentUser.email.split('@')[1];
            const userDomain = user.email ? user.email.split('@')[1] : '';
            return matchesSearch && (userDomain === myDomain);
        }
    });

    return (
        <div className="page-container">
            {canEdit && editingUser && <EditUserModal user={editingUser} closeModal={() => setEditingUser(null)} refreshList={fetchAllUsers} />}
            {canManageAdmins && showAddAdminModal && <AddGlobalAdminModal closeModal={() => setShowAddAdminModal(false)} refreshList={fetchAllUsers} />}
            
            <div className="page-header">
                <h1>{canViewAll ? "Global Users Directory" : "Staff & Users Directory"}</h1>
                {canManageAdmins && (
                    <button className="add-button" onClick={() => setShowAddAdminModal(true)}>+ Add Global Super Admin</button>
                )}
            </div>

            <div className="form-group full-width" style={{ marginBottom: '1.5rem' }}>
                <input type="text" placeholder="Search by name, email, or role..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '10px', width: '100%', borderRadius: '5px', border: '1px solid #ccc' }} />
            </div>

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Full Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="6" className="no-data-cell">Loading...</td></tr>
                        ) : filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => {
                                const isUserActive = user.active !== undefined ? user.active === true : user.status === true;

                                return (
                                    <tr key={user.id}>
                                        <td>{user.username}</td>
                                        <td>{user.fullName}</td>
                                        <td>{user.email}</td>
                                        <td><span className={`role-badge ${user.role ? user.role.toLowerCase() : 'default'}`}>{user.role}</span></td>
                                        
                                        <td><span className={isUserActive ? 'status-active' : 'status-inactive'}>{isUserActive ? 'Active' : 'Suspended'}</span></td>
                                        
                                        <td>
                                            {canEdit ? (
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button className="action-button edit" onClick={() => setEditingUser(user)}>Edit</button>
                                                    
                                                    <button className={`action-button ${isUserActive ? 'danger' : 'success'}`} onClick={() => handleToggleStatus(user)}>
                                                        {isUserActive ? 'Disable' : 'Enable'}
                                                    </button>
                                                    
                                                    {canDelete && (
                                                        <button className="action-button delete" onClick={() => handleDeleteUser(user)}>Delete</button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>View Only</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="6" className="no-data-cell">No users found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default GlobalUsersPage;